import React, { useState, useRef, useEffect, useMemo, createContext, useContext } from 'react';
import MapControlsContainer from '../../../../components/MapControls/MapControlsContainer';
import { useStore, useStorePrimitive, useActions } from '../../../../hooks/useStore';
import { useImageLibraryStore } from '../../../../state/createImageLibraryStore';
import type { RenderedImageRect, Image, ImageCategory } from '../../../../types/types';
import { useGrid } from '../../../../components/GridProvider/useGrid';
import KonvaMapContainer from '../../../../components/KonvaLayers/KonvaMapContainer';
import type { Stage } from 'konva/lib/Stage';
import styles from './MapPanel.module.css';
import { useRenderLoopDetection } from '../../../../hooks/hookUtils';

interface MapPanelProps {
  isResizing: boolean;
  onImageRectChange: (rect: RenderedImageRect | null) => void;
}
interface KonvaMapRef {
  getStage: () => Stage;
  resetView: () => void;
  fitImage: () => void;
}

interface MapActionsContextType {
  resetView: () => void;
  fitImage: () => void;
  konvaMapRef: React.RefObject<KonvaMapRef>;
}

const MapActionsContext = createContext<MapActionsContextType | null>(null);
const useMapActions = () => useContext(MapActionsContext)!;

const MapPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const konvaMapRef = useRef<KonvaMapRef>(null) as React.RefObject<KonvaMapRef>;

  const mapActionsContextValue: MapActionsContextType = useMemo(() => ({
    resetView: () => konvaMapRef.current?.resetView(),
    fitImage: () => konvaMapRef.current?.fitImage(),
    konvaMapRef: konvaMapRef,
  }), []);

  return <MapActionsContext.Provider value={mapActionsContextValue}>{children}</MapActionsContext.Provider>;
};

const MapPanelContent: React.FC<MapPanelProps> = ({ onImageRectChange }) => {
  // Development-time render loop detection for this map container component
  useRenderLoopDetection('MapPanelContent');

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number; } | null>(null);

  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const { konvaMapRef } = useMapActions();

  // Standard useEffect is appropriate here for DOM event subscription (ResizeObserver).
  // This is not async data fetching, so useMountEffect/useAsyncEffect are not needed.
  // The empty deps array is correct since we only need to set up the observer once on mount.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  const imageLibrary = useImageLibraryStore(state => state.images);
  const activeMap = useMemo(() => {
    return (imageLibrary ?? []).find((img: Image) => img.id === activeMapId);
  }, [imageLibrary, activeMapId]);

  return (
    <div id="map-container" ref={containerRef} className={styles.container}>
      {activeMap ? (
        <KonvaMapContainer
          ref={konvaMapRef}
          containerSize={containerSize}
          imageUrl={activeMap.imageUrl}
          onImageRectChange={onImageRectChange}
        />
      ) : (
        <div className={styles.placeholder}>No map selected.</div>
      )}
    </div>
  );
};

const MapPanelControls = () => {
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const imageLibrary = useImageLibraryStore(state => state.images);
  const imageCategories = useStore(state => state.session?.cache?.imageCategories);
  const drawingMode = useStorePrimitive(state => state.session?.map?.drawingMode);
  const actions = useActions();
  const { resetView, fitImage } = useMapActions(); // This will now correctly get the context value
  const { gridSettings } = useGrid();

  const mapImages = useMemo(() => {
    // Find all category IDs that are designated for maps.
    const mapCategoryIds = new Set(
      (imageCategories ?? []).filter((cat: ImageCategory) => cat.isMapCategory).map(cat => cat.id)
    );
    // Filter images that belong to any of the map categories.
    return (imageLibrary ?? []).filter((img: Image) => img.categoryId !== null && mapCategoryIds.has(img.categoryId));
  }, [imageLibrary, imageCategories]);

  return (
    <MapControlsContainer
      onResetView={resetView}
      onFitImage={fitImage}
      drawingMode={drawingMode ?? null}
      setDrawingMode={(mode) => actions.setDrawingMode(mode)}
      maps={mapImages}
      activeMapId={activeMapId}
      onMapChange={(id) => actions.setActiveMap(id)}
      isMapActive={!!activeMapId}
      interaction={null} // This is now handled internally by KonvaMap
      gridSettings={gridSettings}
      saveRegion={() => {}} // This is now handled internally by KonvaMap
    />
  );
}


const MapPanel = Object.assign(MapPanelProvider, { Content: MapPanelContent, Controls: MapPanelControls });
export default MapPanel;
